// Safari grants playback permission to a media element, not to every new Audio().
// Keep this element across cards/routes; Web Audio sound-effect unlocks do not
// unlock HTMLAudioElement playback.
let element = null;
let unlocked = false;
let pendingUnlock = null;
let unlockGeneration = 0;

export function getListeningAudio() {
  if (!element) {
    element = new Audio();
    element.preload = 'auto';
  }
  return element;
}

function silentWaveSource() {
  // 50 ms of actual, unmuted PCM silence. Muting an element only grants muted
  // autoplay, and an empty/invalid source cannot establish playback permission.
  const samples = 400;
  const bytes = new Uint8Array(44 + samples);
  const view = new DataView(bytes.buffer);
  const write = (offset, text) => [...text].forEach((char, index) => { bytes[offset + index] = char.charCodeAt(0); });
  write(0, 'RIFF'); view.setUint32(4, bytes.length - 8, true); write(8, 'WAVE'); write(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, 8000, true); view.setUint32(28, 8000, true); view.setUint16(32, 1, true); view.setUint16(34, 8, true);
  write(36, 'data'); view.setUint32(40, samples, true); bytes.fill(128, 44);
  return `data:audio/wav;base64,${btoa(String.fromCharCode(...bytes))}`;
}

export function unlockListeningAudio() {
  if (unlocked) return Promise.resolve(true);
  if (pendingUnlock) return pendingUnlock;
  // Automatic transitions must not replace the current source with a primer.
  if (typeof navigator !== 'undefined' && navigator.userActivation?.isActive === false) return Promise.resolve(false);
  const audio = getListeningAudio();
  if (audio.src && audio.paused === false && !audio.ended) return Promise.resolve(true);
  const source = silentWaveSource();
  const generation = unlockGeneration;
  audio.src = source;
  let playback;
  try { playback = audio.play(); } catch (_) { return Promise.resolve(false); }
  const attempt = Promise.resolve(playback).then(() => {
    if (element !== audio || generation !== unlockGeneration) return false;
    unlocked = true;
    // A fetched clip may already have replaced the primer. Never pause it.
    if (audio.src === source) { audio.pause(); audio.currentTime = 0; }
    return true;
  }).catch(() => false);
  pendingUnlock = attempt;
  void attempt.finally(() => { if (pendingUnlock === attempt) pendingUnlock = null; });
  return attempt;
}

export function invalidateListeningAudioUnlock(audio) {
  if (audio !== element) return;
  // Permission can be lost after an iOS interruption. Re-prime this same
  // element on the next real tap; an older primer must not restore stale state.
  unlocked = false; pendingUnlock = null; unlockGeneration += 1;
}

export function resetListeningAudio() {
  element?.pause();
  element?.removeAttribute('src');
  element = null; unlocked = false; pendingUnlock = null; unlockGeneration += 1;
}
